import { useMemo } from "react";
import { collectionService } from "@/services";
import type { CollectionDemand, PaymentMode } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatINRNumber } from "@/utils/format";
import { PaymentForm } from "../../components/PaymentForm";

interface BulkReceiptModalProps {
  open: boolean;
  demands: CollectionDemand[];
  onClose: () => void;
  onDone: () => void;
}

export function BulkReceiptModal({ open, demands, onClose, onDone }: BulkReceiptModalProps) {
  const { user } = useAuth();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";

  const [applyLateFees, setApplyLateFees] = useState(false);

  const hasAnyLateFees = useMemo(
    () => demands.some((d) => (d.lateFee || 0) > 0 || (d.applicableLateFee ?? 0) > 0),
    [demands]
  );

  const totalOverdueFeeAmount = useMemo(
    () =>
      demands.reduce((sum, d) => {
        const fee = Math.max(0, d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0));
        return sum + fee;
      }, 0),
    [demands]
  );

  const demandDue = (d: CollectionDemand, applyFee: boolean) => {
    const baseDue = Math.max(0, d.balance - Math.max(0, d.lateFee || 0));
    const fee = Math.max(0, d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0));
    return applyFee ? baseDue + fee : baseDue;
  };

  const byMember = useMemo(() => {
    const map = new Map<string, CollectionDemand[]>();
    for (const d of demands) {
      const arr = map.get(d.memberId) ?? [];
      arr.push(d);
      map.set(d.memberId, arr);
    }
    return [...map.entries()].map(([memberId, list]) => ({
      memberId,
      memberName: list[0].memberName,
      total: list.reduce((s, d) => s + demandDue(d, applyLateFees), 0),
      hasLateFee: list.some((d) => (d.lateFee || 0) > 0 || (d.applicableLateFee ?? 0) > 0),
      list,
    }));
  }, [demands, applyLateFees]);

  const grandTotal = useMemo(() => byMember.reduce((s, m) => s + m.total, 0), [byMember]);

  const submit = async (values: { mode: PaymentMode; reference: string; receivedOn: string; remarks: string }) => {
    try {
      // One recordPayment call per member — mock mode's receipt shape is
      // scoped to a single memberId, so this loop stays even though the real
      // branch could in principle combine them (it can't across memberId anyway).
      const created: string[] = [];
      for (const group of byMember) {
        const { receipts } = await collectionService.recordPayment({
          memberId: group.memberId,
          payments: group.list.map((d) => ({ demandId: d.id, amount: demandDue(d, applyLateFees), groupMemberId: d.groupMemberId })),
          mode: values.mode,
          reference: values.reference,
          receivedOn: values.receivedOn,
          remarks: values.remarks,
          actor,
          applyLateFee: applyLateFees,
        });
        created.push(...receipts.map((r) => r.receiptNumber));
      }
      toast.success("Bulk receipt recorded", `${created.length} receipt${created.length === 1 ? "" : "s"} issued (${created.join(", ")}).`);
      onDone();
      return true;
    } catch (err) {
      toast.error("Could not record bulk payment", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk receipt"
      description="Issue one receipt per selected member, paying their full outstanding balance."
      size="lg"
    >
      <div className="space-y-4">
        {hasAnyLateFees && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <i className="ri-timer-flash-line text-base" />
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-900">
                  Late fees available ({formatINRNumber(totalOverdueFeeAmount)})
                </p>
                <p className="text-xs text-amber-700">
                  {applyLateFees
                    ? "Late fees will apply to overdue installments in this bulk collection."
                    : "Late fees will NOT apply. Only base installments will be collected."}
                </p>
              </div>
            </div>
            <label className="flex flex-none items-center gap-2 cursor-pointer select-none rounded-md bg-white px-2.5 py-1.5 border border-amber-200 shadow-sm hover:bg-amber-50/50">
              <input
                type="checkbox"
                checked={applyLateFees}
                onChange={(e) => setApplyLateFees(e.target.checked)}
                className="h-4 w-4 rounded border-amber-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <span className="text-xs font-medium text-foreground-800">
                {applyLateFees ? "Apply late fees" : "Don't apply fees"}
              </span>
            </label>
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-background-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100/80 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">Member</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">Demands</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Full amount</th>
              </tr>
            </thead>
            <tbody>
              {byMember.map((g) => (
                <tr key={g.memberId} className="border-t border-background-100">
                  <td className="px-3 py-2.5 font-medium text-foreground-900">{g.memberName}</td>
                  <td className="px-3 py-2.5 text-xs text-foreground-500">
                    {g.list.map((d) => `${d.groupName} · cycle ${d.cycle}`).join(", ")}
                  </td>
                  <td className="tnum px-3 py-2.5 text-right font-semibold text-foreground-900">
                    <CurrencyDisplay amount={g.total} hideSymbol />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-background-200 bg-background-100/60">
                <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">Total</td>
                <td />
                <td className="tnum px-3 py-2 text-right font-semibold text-foreground-900">
                  <CurrencyDisplay amount={grandTotal} hideSymbol />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <PaymentForm
          onSubmit={submit}
          submitLabel={`Issue ${byMember.length} receipt${byMember.length === 1 ? "" : "s"}`}
          submitIcon="ri-receipt-line"
          amountSummary={
            <span className="text-sm text-foreground-600">
              Collecting <span className="tnum font-semibold text-foreground-900">{formatINRNumber(grandTotal)}</span> in total
            </span>
          }
        />
      </div>
    </Modal>
  );
}